import Button from "@mui/material/Button";
import ResponsiveDialog from "../_atoms/ResponsiveDialog";
import { useMoveFileToPatientModal } from "../../store/modal-context";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Alert, Box, ButtonBase, Stack, Typography } from "@mui/material";
import PatientBrowser from "../patients/PatientBrowser";
import useApiHook from "../../hooks/useApiHook";
import { useEffect, useState } from "react";
import { useCreatePatientModal } from "../../store/modal-context";
import cancelButtonSx from "../../styles/cancelButtonSx";

const MoveFileToPatientDialog = () => {
    const { isOpen, closeModal, payload, openModal } = useMoveFileToPatientModal();
    const createPatientModal = useCreatePatientModal();
    const {
        response: moveResponse,
        makeRequest: makeMoveRequest,
        loading: moveLoading,
        error: moveError
    } = useApiHook();
    const [batchMoveError, setBatchMoveError] = useState(null);
    const [closeWarning, setCloseWarning] = useState(null);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [submitRequested, setSubmitRequested] = useState(false);
    const fileIdsToMove = Array.isArray(payload?.fileIds) ? payload.fileIds.filter(Boolean) : [];
    const isBatchMove = fileIdsToMove.length > 0;
    const isConfirmDisabled = moveLoading
        || submitRequested
        || (!payload?.selectOnly && !payload?.fileId && !isBatchMove)
        || (payload?.requireSelection && !selectedPatientId);
    const moveErrorMessage = batchMoveError || (moveError ? (payload?.errorMessage || 'Impossibile spostare il file. Riprova.') : null);
    const showInlineCreatePatientAction = payload?.showInlineCreatePatientAction === true;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedPatientId(payload?.patientId ?? null);
        setSelectedPatient(payload?.patientId ? { id: payload.patientId, title: payload?.patientName || payload?.currentPatientName || null } : null);
        setSubmitRequested(false);
        setBatchMoveError(null);
        setCloseWarning(null);
    }, [isOpen, payload?.currentPatientName, payload?.patientId, payload?.patientName]);

    useEffect(() => {
        if (!isOpen || !submitRequested || isBatchMove || !moveResponse || moveResponse.state !== 'success') {
            return;
        }

        if (payload?.onSuccess) {
            payload.onSuccess();
        }

        setSubmitRequested(false);
        closeModal();
    }, [closeModal, isBatchMove, isOpen, moveResponse, payload, submitRequested]);

    const onSelectHandler = (item, nextSelectedPatientId) => {
        setSelectedPatientId(nextSelectedPatientId);
        setSelectedPatient(nextSelectedPatientId ? item : null);
    };

    const reopenMoveDialog = (patientId = selectedPatientId, patientName = selectedPatient?.title ?? null) => {
        openModal({
            ...payload,
            patientId,
            patientName,
        });
    };

    const handleCreatedPatient = (createdPatient) => {
        const nextPatientId = createdPatient?.id ?? null;
        const nextPatientName = createdPatient?.name ?? null;

        setSelectedPatientId(nextPatientId);
        setSelectedPatient(nextPatientId ? {
            id: nextPatientId,
            title: nextPatientName
        } : null);

        reopenMoveDialog(nextPatientId, nextPatientName);
    };

    const onCreatePatientClickHandler = () => {
        createPatientModal.openModal({
            navigateOnSuccess: false,
            onSuccess: handleCreatedPatient,
            onCancel: () => reopenMoveDialog(),
        });
    };

    const onCloseHandler = () => {
        if (payload?.preventClose) {
            setCloseWarning(
                payload?.closeWarningMessage
                || "Per analizzare le sedute e monitorare i percorsi dei pazienti devi prima assegnare i file a un paziente."
            );
            return;
        }

        setSubmitRequested(false);
        setCloseWarning(null);

        if (payload?.onCancel) {
            payload.onCancel();
        }

        closeModal();
    };

    const onConfirmHandler = () => {
        if (payload?.requireSelection && !selectedPatientId) {
            return;
        }

        setCloseWarning(null);

        if (payload?.selectOnly) {
            if (payload?.onSuccess) {
                payload.onSuccess(selectedPatient ? {
                    id: selectedPatientId,
                    name: selectedPatient.title
                } : null);
            }

            setSubmitRequested(false);
            closeModal();
            return;
        }

        if (isBatchMove) {
            setSubmitRequested(true);
            setBatchMoveError(null);

            Promise.all(
                fileIdsToMove.map((fileId) => makeMoveRequest('/folder/move-file', 'post', {
                    fileId,
                    folderId: selectedPatientId
                }))
            )
                .then((results) => {
                    const hasError = results.some((result) => !result || result.state !== 'success');

                    if (hasError) {
                        setSubmitRequested(false);
                        setBatchMoveError(payload?.errorMessage || 'Impossibile assegnare i file alla cartella. Riprova.');
                        return;
                    }

                    if (payload?.onSuccess) {
                        payload.onSuccess({
                            id: selectedPatientId,
                            name: selectedPatient?.title ?? payload?.patientName ?? null
                        });
                    }

                    setSubmitRequested(false);
                    closeModal();
                });
            return;
        }

        setSubmitRequested(true);
        makeMoveRequest('/folder/move-file', 'post', {
            fileId: payload?.fileId,
            folderId: selectedPatientId
        });
    };

    const renderDescription = () => {
        if (showInlineCreatePatientAction) {
            return (
                <>
                    Seleziona o{' '}
                    <Box
                        component="button"
                        type="button"
                        onClick={onCreatePatientClickHandler}
                        sx={{
                            border: 0,
                            background: 'transparent',
                            p: 0,
                            m: 0,
                            color: 'inherit',
                            font: 'inherit',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        crea una cartella paziente
                    </Box>
                    {' '}per permettere l'analisi con l'Assistente AI.
                </>
            );
        }

        return payload?.description || (
            <>Stai spostando il file <strong>{payload?.fileName}</strong>.</>
        );
    };

    return (
        <ResponsiveDialog
            title={(
                <Stack direction="row" alignItems="baseline" spacing={0.8} flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {payload?.title || 'Sposta file'}
                    </Typography>
                    {payload?.helperText ? (
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.78rem'
                            }}
                        >
                            ({payload.helperText})
                        </Typography>
                    ) : null}
                </Stack>
            )}
            isOpen={isOpen}
            onClose={onCloseHandler}
            preventBackdropClick={payload?.preventClose}
            minWidth="520px"
            minHeight="unset"
            paperSx={{ borderRadius: 1 }}
            titleSx={{ py: 1.6, px: 3, borderBottom: 'none' }}
            contentSx={{ px: { xs: 2, sm: 3 }, py: 2, width: '100%', boxSizing: 'border-box' }}
            actionsSx={{ px: 3, py: 1.5, justifyContent: 'flex-end', gap: 1.5 }}
            actions={(
                <>
                    <Button
                        variant="contained"
                        onClick={onConfirmHandler}
                        disabled={isConfirmDisabled}
                        sx={{
                            minWidth: 156,
                            height: 38,
                            px: 1.8,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            backgroundColor: isConfirmDisabled ? '#e5e5e5' : '#1976d2',
                            color: isConfirmDisabled ? '#9b9b9b' : '#fff',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: isConfirmDisabled ? '#dddddd' : '#1565c0',
                                boxShadow: 'none'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#e5e5e5',
                                color: '#b0b0b0'
                            }
                        }}
                    >
                        {payload?.selectOnly && payload?.confirmLabel === 'Seleziona e carica' && selectedPatientId
                            ? 'Carica'
                            : (payload?.confirmLabel || 'Seleziona')}
                    </Button>
                    {!payload?.preventClose ? (
                        <Button variant="outlined" onClick={onCloseHandler} sx={{ ...cancelButtonSx, minWidth: 96, height: 44 }}>
                            Annulla
                        </Button>
                    ) : null}
                </>
            )}
        >
            <Stack width="100%" height="100%" flexGrow={1} alignItems="stretch">
                <div data-file-id={payload?.fileId ?? ""} />
                {payload?.selectionLabel ? (
                    <Typography width="100%" textAlign="left" variant="h6">
                        {payload.selectionLabel}
                    </Typography>
                ) : null}
                <Stack
                    spacing={1.2}
                    sx={{ width: '100%', alignSelf: 'stretch', mb: 2.1, mt: 0.1 }}
                >
                    <Typography
                        textAlign="left"
                        variant="body2"
                        sx={{ opacity: 0.65, lineHeight: 1.35, fontSize: '0.9rem' }}
                    >
                        {renderDescription()}
                    </Typography>
                    <ButtonBase
                        aria-label="crea cartella paziente"
                        onClick={onCreatePatientClickHandler}
                        sx={{
                            alignSelf: 'flex-start',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            borderRadius: 1,
                            px: 0.4,
                            py: 0.25
                        }}
                    >
                        <Box position="relative" width={24} height={22}>
                            <PersonOutlineIcon
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    fontSize: '1.95rem',
                                    color: '#f6bf45'
                                }}
                            />
                            <AddCircleIcon
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -6,
                                    fontSize: '1.05rem',
                                    color: '#4f8df7',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%'
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                textDecoration: 'underline',
                                fontWeight: 500
                            }}
                        >
                            Crea Paziente
                        </Typography>
                    </ButtonBase>
                </Stack>
                {moveErrorMessage && (
                    <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                        {moveErrorMessage}
                    </Alert>
                )}
                {closeWarning && (
                    <Alert severity="warning" sx={{ mt: moveErrorMessage ? 1.5 : 2, width: "100%" }}>
                        {closeWarning}
                    </Alert>
                )}
                <Stack mt={0.25} width="100%" alignItems="center">
                    <PatientBrowser
                        isActive={isOpen}
                        showCreateAction={false}
                        createActionPlacement="external"
                        selectionMode="single"
                        boxed
                        compactList
                        width="100%"
                        selectedPatientId={selectedPatientId}
                        onSelect={onSelectHandler}
                    />
                </Stack>
            </Stack>
        </ResponsiveDialog>
    );
};

export default MoveFileToPatientDialog;
